import { useRef, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFeatureResumeContentFromSection } from 'approot/feature-flags/use-feature-resume-content-from-section';

/**
 * Determines whether the resume snackbar should be shown on present load
 *
 * Reads the `?resume=true` query param (captured once on mount so navigation
 * can't re-trigger it), checks the feature flag, and strips the param from
 * the URL history entry the first time the snackbar is shown. Returns
 * `dismissResumeSnackbar` to hide it when the user navigates away.
 */
export const useResumeSnackbarVisibility = ({
  currentSectionOverviewIndex,
}: {
  currentSectionOverviewIndex: number;
}) => {
  const featureResumeLesson = useFeatureResumeContentFromSection();
  const [searchParams, setSearchParams] = useSearchParams();
  const [resumeParam] = useState<string | null>(() =>
    searchParams.get('resume')
  );
  const [resumeSnackbarActive, setResumeSnackbarActive] =
    useState<boolean>(true);
  const didStripResumeRef = useRef<boolean>(false);

  const shouldShowResumeSnackbar =
    featureResumeLesson &&
    resumeParam === 'true' &&
    currentSectionOverviewIndex >= 0 &&
    resumeSnackbarActive;

  // Strip ?resume from the URL once (replace so Back doesn't restore it)
  useEffect(() => {
    if (!shouldShowResumeSnackbar || didStripResumeRef.current) return;
    didStripResumeRef.current = true;
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete('resume');
        return next;
      },
      { replace: true }
    );
  }, [shouldShowResumeSnackbar]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismissResumeSnackbar = () => setResumeSnackbarActive(false);

  return { shouldShowResumeSnackbar, dismissResumeSnackbar };
};
